import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, CreditCard } from "lucide-react"
import { listPlans, startCheckout } from "@/lib/api"
import type { Plan } from "@/types"

export default function CreditDialog() {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState<string>("")

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setReason(detail?.message || "You need more credit to run this render.")
      setOpen(true)
    }
    window.addEventListener("atlas:open-credit-dialog", handler)
    return () => window.removeEventListener("atlas:open-credit-dialog", handler)
  }, [])

  useEffect(() => {
    if (!open) return
    const t = (window as unknown as Record<string, unknown>).__atlas_token__ as string
    if (t) setToken(t)
  }, [open])

  useEffect(() => {
    if (!open || !token) return
    setLoading(true)
    listPlans(token)
      .then((data) => setPlans((data.plans || []).filter((p: Plan) => p.kind === "credits")))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false))
  }, [open, token])

  const handleCheckout = async (sku: string) => {
    if (!token) return
    try {
      const data = await startCheckout(sku, token)
      if (data.checkoutUrl) location.assign(data.checkoutUrl)
    } catch {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Checkout could not be started" } }))
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(8, 9, 13, 0.8)" }}
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg surface-elevated p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="eyebrow mb-2">Add credit</p>
                <h2 className="heading-display text-2xl">Top up to keep rendering.</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-[#1e2130] transition-colors">
                <X className="w-5 h-5 text-[#6b7280]" />
              </button>
            </div>
            <p className="text-sm text-[#9ca3af] mb-6">{reason} Nothing was charged and no render was started.</p>

            {loading ? (
              <p className="text-sm text-[#9ca3af]">Loading options…</p>
            ) : plans.length === 0 ? (
              <p className="text-sm text-[#9ca3af]">Credit packs are not configured yet. Contact support and we will sort it out.</p>
            ) : (
              <div className="space-y-2 mb-6">
                {plans.map((plan) => (
                  <button
                    key={plan.sku}
                    onClick={() => handleCheckout(plan.sku)}
                    disabled={!plan.available}
                    className="w-full flex items-center justify-between p-4 rounded-lg border border-[#1e2130] bg-[#0f1117] hover:border-[#2d3148] transition-colors disabled:opacity-40"
                  >
                    <div className="text-left">
                      <b className="text-sm text-[#f0f1f5]">{plan.name}</b>
                      <p className="text-xs text-[#6b7280]">{plan.available ? "Pay once · no subscription" : "Unavailable"}</p>
                    </div>
                    <CreditCard className="w-4 h-4 text-[#9ca3af]" />
                  </button>
                ))}
              </div>
            )}

            <p className="text-xs text-[#6b7280]">Payment is handled by Stripe. Atlas never sees your card details.</p>
            <div className="flex justify-end mt-4">
              <button onClick={() => setOpen(false)} className="btn-subtle">Not now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
